import { lazy } from 'react';
import {
  clearChunkReloadFlag,
  hasAlreadyReloadedForChunkError,
  isChunkLoadError,
  tryReloadOnceForChunkError,
} from '@/utils/chunk-load-error';

/**
 * React.lazy wrapper that recovers from stale chunk URLs after a deploy
 * by hard-reloading once instead of bubbling into the Sentry crash screen.
 */
function lazyWithRetry(factory: Parameters<typeof lazy>[0]) {
  return lazy(async () => {
    try {
      const module = await factory();
      // A successful chunk load means recovery worked — allow a later deploy to auto-reload again
      clearChunkReloadFlag();
      return module;
    } catch (error) {
      if (isChunkLoadError(error) && !hasAlreadyReloadedForChunkError()) {
        tryReloadOnceForChunkError();
        // Never resolve — the page is navigating away
        return new Promise(() => {});
      }
      throw error;
    }
  });
}

// Dashboard
export const DashboardPage = lazyWithRetry(() => import('@/features/dashboard/pages/DashboardPage'));

// Authentication
export const LoginPage = lazyWithRetry(() => import('@/features/auth/pages/LoginPage'));
export const ChangePasswordPage = lazyWithRetry(() => import('@/features/auth/pages/ChangePasswordPage'));
export const AutoLoginPage = lazyWithRetry(() => import('@/features/auth/pages/AutoLoginPage'));

// Survey/Assessment Pages
export const AssessmentPage = lazyWithRetry(() => import('@/features/survey/pages/AssessmentPage'));
export const CreateAssessmentPage = lazyWithRetry(() => import('@/features/survey/pages/CreateAssessmentPage'));
export const CreatePresetAssessmentPage = lazyWithRetry(() => import('@/features/survey/pages/CreatePresetAssessmentPage'));
export const EditAssessmentPage = lazyWithRetry(() => import('@/features/survey/pages/EditAssessmentPage'));
export const ReadAssessmentDetails = lazyWithRetry(() => import('@/features/survey/pages/ReadAssessmentDetails'));

// Participant Pages
export const AddParticipants = lazyWithRetry(() => import('@/features/participants/pages/AddParticipants'));
export const EditParticipant = lazyWithRetry(() => import('@/features/participants/pages/EditParticipant'));
export const ParticipantSearchPage = lazyWithRetry(() => import('@/features/participants/pages/ParticipantSearch'));
export const ParticipantPrioritySearchPage = lazyWithRetry(() => import('@/features/participants/pages/ParticipantPrioritySearch'));

// Organization Pages
export const UpdateOrganizationPage = lazyWithRetry(() => import('@/features/org/pages/update-organization'));
export const ManageChampionsPage = lazyWithRetry(() => import('@/features/org/pages/manage-champions'));
export const ListGroupPage = lazyWithRetry(() => import('@/features/org/pages/list-group'));
export const UpdateGroupPage = lazyWithRetry(() => import('@/features/org/pages/update-group'));
export const CreateGroupPage = lazyWithRetry(() => import('@/features/org/pages/create-group'));
export const CreateOrganizationPage = lazyWithRetry(() => import('@/features/org/pages/create-organization'));
export const ListDistrictsPage = lazyWithRetry(() => import('@/features/org/pages/list-districts'));

// Admin Pages
export const AdminUsersPage = lazyWithRetry(() => import('@/features/admin/pages/admin-users'));
export const IndicatorsListPage = lazyWithRetry(() => import('@/features/admin/pages/indicators-list'));

// Report Pages
export const OverallReportPage = lazyWithRetry(() => import('@/features/report/pages/OverallReportPage'));
export const CombinedReportPage = lazyWithRetry(() => import('@/features/report/pages/CombinedReportPage'));

// Guideline Pages
export const GuidelinePage = lazyWithRetry(() => import('@/features/guideline/pages/GuidelinePage'));

// Participant Survey Pages (public facing)
export const ParticipantAccessPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantAccessPage'));
export const ParticipantIntroPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantIntroPage'));
export const ParticipantVerifyPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantVerifyPage'));
export const ParticipantAnonymousPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantAnonymousPage'));
export const ParticipantConsentPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantConsentPage'));
export const ParticipantDemographicsPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantDemographicsPage'));
export const ParticipantQuestionsPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantQuestionsPage'));
export const ParticipantCompletePage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantCompletePage'));
export const ParticipantSelfRegistrationPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ParticipantSelfRegistrationPage'));
export const ProctorPage = lazyWithRetry(() => import('@/features/participant-survey/pages/ProctorPage'));

// Wellness Center Pages (public facing)
export const WellnessCenterAccessPage = lazyWithRetry(() => import('@/features/wellness-center/pages/WellnessCenterAccessPage'));
export const WellnessCenterHomePage = lazyWithRetry(() => import('@/features/wellness-center/pages/WellnessCenterHomePage'));
export const WellnessCenterReportPage = lazyWithRetry(() => import('@/features/wellness-center/pages/WellnessCenterReportPage'));

// Wellness Center Components
export const WellnessCenterVerification = lazyWithRetry(() => import('@/features/wellness-center/components/WellnessCenterVerification'));
